/**
 * UsersView Component
 * Extracted from HubDashboard.jsx for better code organization
 * Handles: User listing, creation, editing, deletion, pagination, search & filtering
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, UserCheck, Clock, ShieldCheck, Plus, Search,
  ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  User, X, RefreshCw, LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Connected apps - must match HubDashboard
const APPS = [
  { id: 'ridero', name: 'Ridero' },
  { id: 'mechanic_kiosk', name: 'Kiosk' },
  { id: 'pti_pector', name: 'PTI' },
  { id: 'mcanick_scout', name: 'Mcnk Ai' },
  { id: 'mr_mathematico', name: 'Mr.Math' },
  { id: 'fleet_share', name: 'Fleet Share' },
];

export default function UsersView() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role_id: 'user' });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  
  // Pagination & Sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const USERS_PER_PAGE = 20;

  const fetchUsers = async (page = 1, search = '', role = 'all', sort = 'created_at', order = 'desc') => {
    setLoading(true);
    try {
      const skip = (page - 1) * USERS_PER_PAGE;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: USERS_PER_PAGE.toString(),
        sort_by: sort,
        sort_order: order
      });
      if (search) params.append('search', search);
      if (role && role !== 'all') params.append('role_id', role);
      
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/users?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/roles`, { withCredentials: true })
      ]);
      
      setUsers(usersRes.data.users || []);
      setTotalUsers(usersRes.data.total || 0);
      setTotalPages(usersRes.data.pages || 1);
      setRoles(rolesRes.data);
    } catch (e) {
      console.error('Failed to load users', e);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, searchTerm, filterRole, sortBy, sortOrder);
  }, [currentPage, sortBy, sortOrder]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, searchTerm, filterRole, sortBy, sortOrder);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterRole]);

  const handleSortChange = (value) => {
    const [field, order] = value.split('-');
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/users/create`, newUser, { withCredentials: true });
      toast.success('User created successfully!');
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', password: '', role_id: 'user' });
      fetchUsers(1, searchTerm, filterRole, sortBy, sortOrder);
      setCurrentPage(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role_id: user.role_id || 'user',
      is_active: user.is_active !== false,
      is_approved: user.is_approved !== false,
      points_balance: user.points_balance || 0,
      connected_apps: user.connected_apps || [],
      new_password: ''
    });
    setShowEditModal(true);
  };

  const saveUser = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        role_id: editForm.role_id,
        is_active: editForm.is_active,
        is_approved: editForm.is_approved,
        points_balance: parseInt(editForm.points_balance) || 0,
        connected_apps: editForm.connected_apps || []
      };
      
      // Only include password if set
      if (editForm.new_password && editForm.new_password.length >= 6) {
        updateData.password = editForm.new_password;
      }

      await axios.patch(`${API}/users/${selectedUser.user_id}`, updateData, { withCredentials: true });
      toast.success('User updated successfully!');
      setShowEditModal(false);
      fetchUsers(currentPage, searchTerm, filterRole, sortBy, sortOrder);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      setShowEditModal(false);
      fetchUsers(currentPage, searchTerm, filterRole, sortBy, sortOrder);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const revokeUserSessions = async (userId, userName) => {
    if (!window.confirm(`Force logout "${userName}"? All their active sessions will be terminated.`)) return;
    try {
      const res = await axios.post(`${API}/admin/revoke-sessions/${userId}`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Sessions revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke sessions');
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/25 to-blue-600/10 rounded-2xl p-6 border border-blue-500/30 text-center">
          <Users className="w-12 h-12 text-blue-400 mx-auto mb-3" />
          <p className="text-3xl font-bold text-blue-400">{totalUsers}</p>
          <p className="text-sm text-blue-400/70">Total Users</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/25 to-green-600/10 rounded-2xl p-6 border border-green-500/30 text-center">
          <UserCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-3xl font-bold text-green-400">{users.filter(u => u.is_approved).length}</p>
          <p className="text-sm text-green-400/70">Active</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500/25 to-yellow-600/10 rounded-2xl p-6 border border-yellow-500/30 text-center">
          <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
          <p className="text-3xl font-bold text-yellow-400">{users.filter(u => !u.is_approved).length}</p>
          <p className="text-sm text-yellow-400/70">Pending</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/25 to-purple-600/10 rounded-2xl p-6 border border-purple-500/30 text-center">
          <ShieldCheck className="w-12 h-12 text-purple-400 mx-auto mb-3" />
          <p className="text-3xl font-bold text-purple-400">{users.filter(u => u.role_id === 'master_admin' || u.role_id === 'admin').length}</p>
          <p className="text-sm text-purple-400/70">Admins</p>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-card/60 backdrop-blur-xl rounded-3xl border border-border/30 overflow-hidden">
        <div className="p-4 border-b border-border/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold">All Users</h3>
              <span className="text-sm text-muted-foreground">
                (Showing {users.length} of {totalUsers})
              </span>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2" data-testid="create-user-btn">
              <Plus className="w-4 h-4" /> Create User
            </Button>
          </div>
          
          {/* Search, Filter & Sort */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                data-testid="users-search-input"
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
              />
            </div>
            <select
              value={filterRole}
              onChange={e => { setFilterRole(e.target.value); setCurrentPage(1); }}
              data-testid="users-role-filter"
              className="px-4 py-2 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            >
              <option value="all">All Roles</option>
              {roles.map(r => (
                <option key={r.role_id} value={r.role_id}>{r.name || r.role_id}</option>
              ))}
            </select>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={e => handleSortChange(e.target.value)}
              data-testid="users-sort-select"
              className="px-4 py-2 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="email-asc">Email (A-Z)</option>
              <option value="points_balance-desc">Most Points</option>
              <option value="points_balance-asc">Least Points</option>
              <option value="last_login-desc">Recently Active</option>
              <option value="role_id-asc">Role (A-Z)</option>
            </select>
          </div>
        </div>
        
        <div className="divide-y divide-border/30 max-h-[500px] overflow-y-auto">
          {users.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No users found</p>
            </div>
          ) : users.map((u) => (
            <div 
              key={u.user_id} 
              data-testid={`user-row-${u.user_id}`}
              className="p-4 flex items-center gap-4 hover:bg-accent/5 transition-colors cursor-pointer"
              onClick={() => openEditModal(u)}
            >
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                <User className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{u.name}</p>
                <p className="text-sm text-muted-foreground truncate">{u.email}</p>
              </div>
              <div className="text-sm text-muted-foreground capitalize">
                {u.role_id === 'master_admin' ? 'Admin' : u.role_id?.replace('_', ' ')}
              </div>
              <div className="text-sm text-muted-foreground">
                {u.points_balance || 0} pts
              </div>
              <div className={`px-3 py-1 rounded-lg text-sm ${u.is_approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {u.is_approved ? 'Active' : 'Pending'}
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border/30 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({totalUsers} users)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg bg-secondary border border-border hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-first"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg bg-secondary border border-border hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg border transition-colors ${
                    page === currentPage 
                      ? 'bg-accent text-white border-accent' 
                      : 'bg-secondary border-border hover:bg-accent/10'
                  }`}
                  data-testid={`pagination-page-${page}`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg bg-secondary border border-border hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg bg-secondary border border-border hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-last"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Create New User</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Full Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Role</label>
                <select
                  value={newUser.role_id}
                  onChange={e => setNewUser({...newUser, role_id: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                >
                  {roles.filter(r => r.role_id !== 'master_admin').map(r => (
                    <option key={r.role_id} value={r.role_id}>{r.name || r.role_id}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={createUser} disabled={creating} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50">
                {creating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal - Full Edit */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Edit User</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Basic Information</h4>
                <div>
                  <label className="text-sm text-muted-foreground">Full Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm({...editForm, name: e.target.value})}
                    className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={e => setEditForm({...editForm, email: e.target.value})}
                    className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  />
                </div>
              </div>

              {/* Role & Status */}
              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Role & Status</h4>
                <div>
                  <label className="text-sm text-muted-foreground">Role</label>
                  <select
                    value={editForm.role_id}
                    onChange={e => setEditForm({...editForm, role_id: e.target.value})}
                    className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                    disabled={selectedUser.role_id === 'master_admin'}
                  >
                    {roles.map(r => (
                      <option key={r.role_id} value={r.role_id}>{r.name || r.role_id}</option>
                    ))}
                  </select>
                  {selectedUser.role_id === 'master_admin' && (
                    <p className="text-xs text-yellow-400 mt-1">Master admin role cannot be changed</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    onClick={() => setEditForm({...editForm, is_active: !editForm.is_active})}
                    className={`p-4 rounded-xl cursor-pointer border-2 transition-colors ${editForm.is_active ? 'border-green-500 bg-green-500/10' : 'border-border bg-secondary'}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${editForm.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <span className="font-medium">Active</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">User can log in</p>
                  </div>
                  <div 
                    onClick={() => setEditForm({...editForm, is_approved: !editForm.is_approved})}
                    className={`p-4 rounded-xl cursor-pointer border-2 transition-colors ${editForm.is_approved ? 'border-blue-500 bg-blue-500/10' : 'border-border bg-secondary'}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${editForm.is_approved ? 'bg-blue-500' : 'bg-gray-500'}`} />
                      <span className="font-medium">Approved</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Account verified</p>
                  </div>
                </div>
              </div>

              {/* Points */}
              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Points & Rewards</h4>
                <div>
                  <label className="text-sm text-muted-foreground">Points Balance</label>
                  <input
                    type="number"
                    value={editForm.points_balance}
                    onChange={e => setEditForm({...editForm, points_balance: e.target.value})}
                    className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                    min="0"
                  />
                </div>
              </div>

              {/* Security */}
              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Security / Password</h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-muted-foreground">Password Status:</span>
                  <span className={`text-sm font-medium ${selectedUser.has_password ? 'text-green-400' : 'text-yellow-400'}`}>
                    {selectedUser.has_password ? '✓ Password Set' : '⚠ No Password'}
                  </span>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Set New Password</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={editForm.new_password}
                      onChange={e => setEditForm({...editForm, new_password: e.target.value})}
                      className="flex-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                        let password = '';
                        for (let i = 0; i < 8; i++) {
                          password += chars.charAt(Math.floor(Math.random() * chars.length));
                        }
                        setEditForm({...editForm, new_password: password});
                      }}
                      className="px-4 py-3 bg-accent/20 hover:bg-accent/30 text-accent rounded-xl font-medium whitespace-nowrap"
                    >
                      Generate
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to keep current password. Minimum 6 characters.</p>
                </div>
              </div>

              {/* Connected Apps */}
              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Connected Apps</h4>
                <div className="grid grid-cols-2 gap-2">
                  {APPS.map(app => (
                    <label key={app.id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(editForm.connected_apps || []).includes(app.id)}
                        onChange={(e) => {
                          const currentApps = editForm.connected_apps || [];
                          if (e.target.checked) {
                            setEditForm({...editForm, connected_apps: [...currentApps, app.id]});
                          } else {
                            setEditForm({...editForm, connected_apps: currentApps.filter(a => a !== app.id)});
                          }
                        }}
                        className="w-4 h-4 rounded accent-accent"
                      />
                      <span className="text-sm">{app.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Select which apps this user can access</p>
              </div>

              {/* User Info (Read-only) */}
              <div className="p-4 rounded-xl bg-secondary/30 space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">User Info</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">User ID:</span>
                    <span className="ml-2 font-mono text-xs">{selectedUser.user_id}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Referral Code:</span>
                    <span className="ml-2">{selectedUser.referral_code || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <span className="ml-2">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Login:</span>
                    <span className="ml-2">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleDateString() : 'Never'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              {selectedUser.role_id !== 'master_admin' && (
                <button 
                  onClick={() => deleteUser(selectedUser.user_id, selectedUser.name)}
                  className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl font-medium hover:bg-red-500/30"
                  data-testid="delete-user-btn"
                >
                  Delete
                </button>
              )}
              <button 
                onClick={() => revokeUserSessions(selectedUser.user_id, selectedUser.name)}
                className="px-4 py-3 bg-orange-500/20 text-orange-400 rounded-xl font-medium hover:bg-orange-500/30 flex items-center gap-2"
                data-testid="revoke-sessions-btn"
              >
                <LogOut className="w-4 h-4" />
                Force Logout
              </button>
              <button onClick={() => setShowEditModal(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={saveUser} disabled={saving} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
